import { useState, useEffect, useRef } from 'react'
import {
  Box,
  VStack,
  HStack,
  SimpleGrid,
  Text,
  Button,
  IconButton,
  Input,
  Textarea,
  Divider,
  Spinner,
  FormControl,
  FormLabel,
  Select,
  Checkbox,
} from '@chakra-ui/react'
import { AddIcon, DeleteIcon } from '@chakra-ui/icons'
import { useSearchParams } from 'react-router-dom'
import MeetingCard, { Meeting } from './MeetingCard'
import { glassCard, inputStyle, labelStyle } from '../styles'
import { apiFetch } from '../utils/apiFetch'

interface MemberOption {
  _id: string
  name?: string
  email: string
}

interface AddForm {
  date: string
  time: string
  location: string
  hosts: { name: string; memberId?: string; isOther?: boolean }[]
  theme: string
  food: string
  notes: string
  beers: { name: string; brewery: string; url: string; foodPairing: string }[]
  participants: { name: string; memberId?: string; isGuest: boolean; isOther?: boolean }[]
}

const emptyAddForm: AddForm = {
  date: '',
  time: '19:00',
  location: 'Wikholm',
  hosts: [{ name: '', isOther: true }, { name: '', isOther: true }],
  theme: '',
  food: '',
  notes: '',
  beers: [],
  participants: [],
}

const Meetings = () => {
  const [user] = useState(() => {
    const data = localStorage.getItem('user')
    return data ? JSON.parse(data) : null
  })
  const [meetings, setMeetings] = useState<Meeting[]>([])
  const [members, setMembers] = useState<MemberOption[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [addForm, setAddForm] = useState<AddForm>(emptyAddForm)
  const [saving, setSaving] = useState(false)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [searchParams] = useSearchParams()
  const highlightMeetingId = searchParams.get('m')

  const isAdmin: boolean = user?.isAdmin ?? false

  useEffect(() => {
    apiFetch('/api/meetings')
      .then(res => res.json())
      .then(data => { setMeetings(data); setLoading(false) })
      .catch(() => setLoading(false))
  }, [])

  useEffect(() => {
    apiFetch('/api/users')
      .then(res => res.json())
      .then(data => setMembers(data))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!loading && scrollRef.current) {
      setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  const addHost = () => setAddForm(form => ({ ...form, hosts: [...form.hosts, { name: '', isOther: true }] }))
  const removeHost = (index: number) => setAddForm(form => ({ ...form, hosts: form.hosts.filter((_, idx) => idx !== index) }))
  const updateHostName = (index: number, value: string) =>
    setAddForm(form => ({ ...form, hosts: form.hosts.map((host, idx) => idx === index ? { ...host, name: value } : host) }))
  const setHostMember = (index: number, memberId: string) => {
    const member = members.find(m => m._id === memberId)
    if (!member) return
    setAddForm(form => ({ ...form, hosts: form.hosts.map((h, idx) => idx === index ? { name: member.name ?? member.email, memberId: member._id, isOther: false } : h) }))
  }
  const setHostOther = (index: number) =>
    setAddForm(form => ({ ...form, hosts: form.hosts.map((h, idx) => idx === index ? { name: '', memberId: undefined, isOther: true } : h) }))

  const addBeer = () => setAddForm(form => ({ ...form, beers: [...form.beers, { name: '', brewery: '', url: '', foodPairing: '' }] }))
  const removeBeer = (index: number) => setAddForm(form => ({ ...form, beers: form.beers.filter((_, idx) => idx !== index) }))
  const updateBeer = (index: number, field: keyof AddForm['beers'][0], value: string) =>
    setAddForm(form => ({ ...form, beers: form.beers.map((beer, idx) => idx === index ? { ...beer, [field]: value } : beer) }))

  const addParticipant = () => setAddForm(form => ({ ...form, participants: [...form.participants, { name: '', memberId: undefined, isGuest: false, isOther: true }] }))
  const removeParticipant = (index: number) => setAddForm(form => ({ ...form, participants: form.participants.filter((_, idx) => idx !== index) }))

  const setParticipantMember = (index: number, memberId: string) => {
    const member = members.find(m => m._id === memberId)
    if (!member) return
    setAddForm(form => ({
      ...form,
      participants: form.participants.map((p, idx) =>
        idx === index ? { name: member.name ?? member.email, memberId: member._id, isGuest: false } : p
      ),
    }))
  }

  const setParticipantOther = (index: number) => {
    setAddForm(form => ({
      ...form,
      participants: form.participants.map((p, idx) =>
        idx === index ? { name: '', memberId: undefined, isGuest: false, isOther: true } : p
      ),
    }))
  }

  const updateParticipantName = (index: number, value: string) =>
    setAddForm(form => ({
      ...form,
      participants: form.participants.map((p, idx) => idx === index ? { ...p, name: value } : p),
    }))

  const toggleParticipantGuest = (index: number) =>
    setAddForm(form => ({
      ...form,
      participants: form.participants.map((p, idx) => idx === index ? { ...p, isGuest: !p.isGuest } : p),
    }))

  const handleAdd = async () => {
    if (!addForm.date) return
    setSaving(true)
    const body = {
      date: addForm.date,
      time: addForm.time,
      location: addForm.location,
      hosts: addForm.hosts.filter(host => host.name.trim()).map(host => ({ name: host.name.trim() })),
      theme: addForm.theme.trim() || undefined,
      food: addForm.food.trim() || undefined,
      notes: addForm.notes.trim() || undefined,
      beers: addForm.beers
        .filter(beer => beer.name.trim())
        .map(beer => ({
          name: beer.name.trim(),
          brewery: beer.brewery.trim() || undefined,
          url: beer.url.trim() || undefined,
          foodPairing: beer.foodPairing.trim() || undefined,
        })),
      participants: addForm.participants
        .filter(participant => participant.name.trim())
        .map(participant => ({
          name: participant.name.trim(),
          memberId: participant.memberId,
          isGuest: participant.isGuest,
        })),
      participantCount: addForm.participants.filter(participant => participant.name.trim()).length,
    }
    const res = await apiFetch('/api/meetings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (res.ok) {
      const created = await res.json()
      setMeetings(prev => [...prev, created].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()))
      setAddForm(emptyAddForm)
      setShowAddForm(false)
    }
    setSaving(false)
  }

  const handleDelete = async (id: string) => {
    const res = await apiFetch(`/api/meetings/${id}`, { method: 'DELETE' })
    if (res.ok) setMeetings(prev => prev.filter(meeting => meeting._id !== id))
  }

  const handleUpdate = (updated: Meeting) => {
    setMeetings(prev => prev.map(meeting => meeting._id === updated._id ? updated : meeting))
  }

  // sorted newest-first, so the last index with date >= today is the closest upcoming meeting
  const todayISO = new Date().toISOString().slice(0, 10)
  const nextMeetingIdx = meetings.reduce((bestIdx, meeting, index) =>
    meeting.date.slice(0, 10) >= todayISO ? index : bestIdx
  , -1)

  return (
    <Box minH="100vh" position="relative" p={{ base: 4, md: 8 }} pt={{ base: 16, md: 20 }}>

      <Box maxW="700px" mx="auto">
        <VStack spacing={6} align="stretch">

          <HStack justifyContent="space-between" alignItems="center">
            <Text fontSize="2xl" fontWeight="bold" color="white">🍻 Meetings</Text>
            {isAdmin && !showAddForm && (
              <Button leftIcon={<AddIcon />} size="sm" bg="whiteAlpha.200" color="white" border="1px solid" borderColor="whiteAlpha.300" _hover={{ bg: 'whiteAlpha.300' }} onClick={() => setShowAddForm(true)}>
                Add Meeting
              </Button>
            )}
          </HStack>

          {/* ── Add meeting form (admin only) ── */}
          {isAdmin && showAddForm && (
            <Box {...glassCard} borderRadius="xl" p={{ base: 4, md: 6 }}>
              <VStack spacing={5} align="stretch">
                <Text fontWeight="semibold" color="white">New Meeting</Text>

                {/* Date / Time / Location */}
                <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3}>
                  <FormControl>
                    <FormLabel {...labelStyle}>Date</FormLabel>
                    <Input type="date" value={addForm.date} onChange={e => setAddForm(f => ({ ...f, date: e.target.value }))} {...inputStyle} sx={{ '::-webkit-calendar-picker-indicator': { filter: 'invert(1)' } }} />
                  </FormControl>
                  <FormControl>
                    <FormLabel {...labelStyle}>Time</FormLabel>
                    <Input value={addForm.time} onChange={e => setAddForm(f => ({ ...f, time: e.target.value }))} {...inputStyle} />
                  </FormControl>
                  <FormControl>
                    <FormLabel {...labelStyle}>Location</FormLabel>
                    <Input value={addForm.location} onChange={e => setAddForm(f => ({ ...f, location: e.target.value }))} {...inputStyle} />
                  </FormControl>
                </SimpleGrid>

                <Divider borderColor="whiteAlpha.100" />

                {/* Hosts */}
                <VStack align="stretch" spacing={2}>
                  <HStack justify="space-between">
                    <Text {...labelStyle}>Hosts</Text>
                    <Button size="xs" variant="ghost" color="orange.300" leftIcon={<AddIcon />} onClick={addHost}>Add</Button>
                  </HStack>
                  {addForm.hosts.map((host, index) => (
                    <VStack key={index} align="stretch" spacing={1}>
                      <HStack spacing={2}>
                        <Select
                          size="sm"
                          value={host.memberId ?? (host.isOther ? '__other__' : '')}
                          onChange={e => {
                            if (e.target.value === '__other__') setHostOther(index)
                            else if (e.target.value) setHostMember(index, e.target.value)
                          }}
                          color="white"
                          borderColor="whiteAlpha.300"
                          bg="transparent"
                          sx={{ option: { background: '#2D3748' } }}
                          flex={1}
                        >
                          {members.filter(m => m.name || m.email).map(m => (
                            <option key={m._id} value={m._id}>{m.name ?? m.email}</option>
                          ))}
                          <option value="__other__">Other (enter name)</option>
                        </Select>
                        {addForm.hosts.length > 1 && (
                          <IconButton aria-label="Remove" icon={<DeleteIcon />} size="xs" variant="ghost" color="red.300" onClick={() => removeHost(index)} />
                        )}
                      </HStack>
                      {host.isOther && (
                        <Input
                          placeholder={`Host ${index + 1} name`}
                          value={host.name}
                          onChange={e => updateHostName(index, e.target.value)}
                          {...inputStyle}
                        />
                      )}
                    </VStack>
                  ))}
                </VStack>

                <Divider borderColor="whiteAlpha.100" />

                {/* Theme + Food */}
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                  <FormControl>
                    <FormLabel {...labelStyle}>Theme (optional)</FormLabel>
                    <Input placeholder="e.g. Blindprovning" value={addForm.theme} onChange={e => setAddForm(f => ({ ...f, theme: e.target.value }))} {...inputStyle} />
                  </FormControl>
                  <FormControl>
                    <FormLabel {...labelStyle}>Food (optional)</FormLabel>
                    <Input placeholder="e.g. Pizza" value={addForm.food} onChange={e => setAddForm(f => ({ ...f, food: e.target.value }))} {...inputStyle} />
                  </FormControl>
                </SimpleGrid>

                <Divider borderColor="whiteAlpha.100" />

                {/* Beers */}
                <VStack align="stretch" spacing={2}>
                  <HStack justify="space-between">
                    <Text {...labelStyle}>Beers Tasted (optional)</Text>
                    <Button size="xs" variant="ghost" color="orange.300" leftIcon={<AddIcon />} onClick={addBeer}>Add Beer</Button>
                  </HStack>
                  {addForm.beers.map((beer, index) => (
                    <Box key={index} bg="whiteAlpha.50" borderRadius="md" p={3}>
                      <HStack spacing={2} align="flex-start">
                        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2} flex={1}>
                          <Input placeholder="Brewery" value={beer.brewery} onChange={e => updateBeer(index, 'brewery', e.target.value)} {...inputStyle} />
                          <Input placeholder="Beer name" value={beer.name} onChange={e => updateBeer(index, 'name', e.target.value)} {...inputStyle} />
                          <Input placeholder="URL" value={beer.url} onChange={e => updateBeer(index, 'url', e.target.value)} {...inputStyle} />
                          <Input placeholder="Food pairing" value={beer.foodPairing} onChange={e => updateBeer(index, 'foodPairing', e.target.value)} {...inputStyle} />
                        </SimpleGrid>
                        <IconButton aria-label="Remove beer" icon={<DeleteIcon />} size="xs" variant="ghost" color="red.300" onClick={() => removeBeer(index)} mt={1} />
                      </HStack>
                    </Box>
                  ))}
                  {addForm.beers.length === 0 && (
                    <Text color="whiteAlpha.400" fontSize="xs" fontStyle="italic">Click "Add Beer" to record which beers were tasted</Text>
                  )}
                </VStack>

                <Divider borderColor="whiteAlpha.100" />

                {/* Attendees */}
                <VStack align="stretch" spacing={2}>
                  <HStack justify="space-between">
                    <Text {...labelStyle}>Attendees (optional)</Text>
                    <Button size="xs" variant="ghost" color="orange.300" leftIcon={<AddIcon />} onClick={addParticipant}>Add</Button>
                  </HStack>
                  <VStack spacing={2} align="stretch">
                    {addForm.participants.map((participant, index) => (
                      <VStack key={index} align="stretch" spacing={1}>
                        <HStack spacing={2}>
                          <Select
                            size="sm"
                            value={participant.memberId ?? (participant.isOther ? '__other__' : '')}
                            onChange={e => {
                              if (e.target.value === '__other__') setParticipantOther(index)
                              else if (e.target.value) setParticipantMember(index, e.target.value)
                            }}
                            color="white"
                            borderColor="whiteAlpha.300"
                            bg="transparent"
                            sx={{ option: { background: '#2D3748' } }}
                            flex={1}
                          >
                            {members
                              .filter(member => member.name || member.email)
                              .filter(member => !addForm.participants.some((participant, participantIndex) =>
                                participant.memberId === member._id && participantIndex !== index
                              ))
                              .sort((memberA, memberB) => (memberA.name ?? memberA.email).localeCompare(memberB.name ?? memberB.email))
                              .map(member => (
                                <option key={member._id} value={member._id}>{member.name ?? member.email}</option>
                              ))}
                            <option value="__other__">Other (enter name)</option>
                          </Select>
                          <IconButton aria-label="Remove" icon={<DeleteIcon />} size="xs" variant="ghost" color="red.300" onClick={() => removeParticipant(index)} />
                        </HStack>
                        {participant.isOther && (
                          <HStack spacing={2} pl={1}>
                            <Input
                              placeholder="Name"
                              value={participant.name}
                              onChange={e => updateParticipantName(index, e.target.value)}
                              {...inputStyle}
                              flex={1}
                            />
                            <Checkbox isChecked={participant.isGuest} onChange={() => toggleParticipantGuest(index)} colorScheme="orange" color="whiteAlpha.600" size="sm" whiteSpace="nowrap">
                              Guest
                            </Checkbox>
                          </HStack>
                        )}
                      </VStack>
                    ))}
                  </VStack>
                  {addForm.participants.length === 0 && (
                    <Text color="whiteAlpha.400" fontSize="xs" fontStyle="italic">Click "Add" to record who attended</Text>
                  )}
                </VStack>

                <Divider borderColor="whiteAlpha.100" />

                {/* Notes */}
                <FormControl>
                  <FormLabel {...labelStyle}>Notes (optional)</FormLabel>
                  <Textarea placeholder="Any notes about this meeting…" value={addForm.notes} onChange={e => setAddForm(f => ({ ...f, notes: e.target.value }))} {...inputStyle} rows={2} />
                </FormControl>

                <HStack justify="flex-end" spacing={3}>
                  <Button size="sm" variant="ghost" color="whiteAlpha.700" onClick={() => { setShowAddForm(false); setAddForm(emptyAddForm) }}>Cancel</Button>
                  <Button size="sm" colorScheme="orange" isLoading={saving} onClick={handleAdd} isDisabled={!addForm.date}>Save Meeting</Button>
                </HStack>
              </VStack>
            </Box>
          )}

          {loading ? (
            <Spinner color="white" />
          ) : meetings.length === 0 ? (
            <Text color="whiteAlpha.600">No meetings scheduled yet.</Text>
          ) : (
            <VStack spacing={4} align="stretch">
              {meetings.map((meeting, idx) => {
                const isScrollTarget = !!highlightMeetingId && meeting._id === highlightMeetingId
                return (
                  <MeetingCard
                    key={meeting._id}
                    meeting={meeting}
                    isAdmin={isAdmin}
                    currentUserId={user?._id}
                    isNext={idx === nextMeetingIdx}
                    scrollRef={isScrollTarget ? (el: HTMLDivElement | null) => { scrollRef.current = el } : undefined}
                    onUpdate={handleUpdate}
                    onDelete={handleDelete}
                  />
                )
              })}
            </VStack>
          )}

        </VStack>
      </Box>
    </Box>
  )
}

export default Meetings
